import { Modal } from '../../../../components/common/Modal';
import { useLanguage } from '../../../../features/language/useLanguage';
import { formatCurrency } from '../../../../utils/format';

/** A line item whose unit price is recomputed because received weight < ordered weight. */
export interface PriceAdjustmentItem {
  index: number;
  name: string;
  orderedWeight: number;
  receivedWeight: number;
  originalUnitPrice: number;
  adjustedUnitPrice: number;
}

interface UnitPriceAdjustmentModalProps {
  isOpen: boolean;
  items: PriceAdjustmentItem[];
  currencySymbol: string;
  onClose: () => void;
  onKeepOriginal: () => void;
  onApply: () => void;
}

/** Offers to recompute unit prices (total/received) when weight is lost in transit. */
export function UnitPriceAdjustmentModal({
  isOpen,
  items,
  currencySymbol,
  onClose,
  onKeepOriginal,
  onApply,
}: UnitPriceAdjustmentModalProps) {
  const { t } = useLanguage();
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('purchaseOrder.form.modals.unitPriceAdjustmentTitle')} maxWidth="lg">
      <div className="space-y-6">
        {/* Modern Alert Header */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border-l-4 border-amber-400">
          <div className="mt-0.5">
            <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-amber-900">{t('purchaseOrder.form.modals.weightDiscrepancyTitle')}</h3>
            <p className="mt-1 text-sm text-amber-800 leading-relaxed">
              {t('purchaseOrder.form.modals.weightDiscrepancyDescription')}
              <span className="font-mono bg-white/50 px-1 rounded ml-1 italic">
                {t('purchaseOrder.form.modals.totalDivReceivedWeight')}
              </span>
              ?
            </p>
          </div>
        </div>

        {/* Items List */}
        <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
          {items.map((item) => (
            <div
              key={item.index}
              className="group transition-all bg-gray-50 hover:bg-white border border-gray-100 hover:shadow-md rounded-xl p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <span className="text-sm font-bold text-gray-800 uppercase tracking-tight">{item.name}</span>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Weight Section */}
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    {t('purchaseOrder.form.modals.weightComparison')}
                  </p>
                  <div className="flex flex-col text-sm text-gray-600">
                    <span>
                      {t('purchaseOrder.form.modals.ordered')}:{' '}
                      <span className="font-medium text-gray-900">{item.orderedWeight.toFixed(3)} kg</span>
                    </span>
                    <span>
                      {t('purchaseOrder.form.modals.received')}:{' '}
                      <span className="font-medium text-primary-600">{item.receivedWeight.toFixed(3)} kg</span>
                    </span>
                  </div>
                </div>

                {/* Price Section */}
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    {t('purchaseOrder.form.modals.priceAdjustment')}
                  </p>
                  <div className="flex flex-col text-sm">
                    <span className="line-through text-gray-400">{formatCurrency(item.originalUnitPrice, currencySymbol)}</span>
                    <span className="font-bold text-gray-900 text-base">{formatCurrency(item.adjustedUnitPrice, currencySymbol)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          <button onClick={onKeepOriginal} className="px-5 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors">
            {t('purchaseOrder.form.modals.keepOriginal')}
          </button>
          <button
            onClick={onApply}
            className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg shadow-sm shadow-primary-200 transition-all active:scale-95 text-sm font-semibold"
          >
            {t('purchaseOrder.form.modals.applyAdjustments')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
